import React, { useState } from "react";
import { Layout, Sun, Heart, Baby, Plus, Minus, ChevronDown, ChevronUp, Bed, X, Smartphone as Sofa, Columns as Bunk, Joystick } from "lucide-react";
import { Card, Input, Toggle, Textarea, TagGroup, Stepper } from "./FormComponents";

const BedTile = ({ label, value, onChange, icon: Icon, isLight }) => (
  <div className={`flex flex-col gap-4 p-5 border rounded-3xl transition-all shadow-sm ${
    isLight ? "bg-white border-neutral-200 hover:border-blue-500/30" : "bg-white/5 border-white/5 hover:border-blue-400/30"
  }`}>
    <div className="flex items-center gap-3">
      <div className={`h-8 w-8 rounded-xl flex items-center justify-center border transition-all ${isLight ? "bg-neutral-50 border-neutral-200 text-neutral-500" : "bg-white/5 border-white/10 text-neutral-400"}`}>
        <Icon size={16} />
      </div>
      <span className={`text-[10px] uppercase tracking-[0.18em] font-bold ${isLight ? "text-neutral-500" : "text-neutral-400"}`}>{label}</span>
    </div>
    <div className="flex items-center justify-between gap-4 mt-2">
      <button
        type="button"
        onClick={() => onChange(Math.max(0, value - 1))}
        className={`h-9 w-9 rounded-full border flex items-center justify-center transition-all active:scale-90 ${
          isLight ? "border-neutral-200 hover:bg-neutral-100 text-neutral-600" : "border-white/10 hover:bg-white/10 text-neutral-300"
        }`}
      >
        <Minus size={16} />
      </button>
      <span className={`text-2xl font-bold tracking-tight ${isLight ? "text-neutral-900" : "text-neutral-100"}`}>{value}</span>
      <button
        type="button"
        onClick={() => onChange(value + 1)}
        className="h-9 w-9 rounded-full bg-blue-500 text-white flex items-center justify-center hover:bg-blue-400 transition-all shadow-lg shadow-blue-500/20 active:scale-90"
      >
        <Plus size={16} />
      </button>
    </div>
  </div>
);

export const Step9 = ({ data, updateData, isACAvailable, isCeilingFanAvailable, isLight, showValidation }) => {
  const [showMoreBeds, setShowMoreBeds] = useState({});

  const handleChange = (field, value) => {
    updateData("livingSpaces", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("livingSpaces", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  const handleBedroomsCountChange = (val) => {
    const count = val === "" ? 0 : val;
    let newRooms = [...data.rooms];
    if (newRooms.length < count) {
      for (let i = newRooms.length; i < count; i++) {
        newRooms.push({
          id: (Date.now() + i).toString(),
          beds: { twin: 0, full: 0, queen: 0, king: 0, sofa: 0, bunk: 0 },
          features: [],
          otherFeatures: []
        });
      }
    } else if (newRooms.length > count) {
      newRooms = newRooms.slice(0, count);
    }
    updateData("livingSpaces", { ...data, bedrooms: val, rooms: newRooms });
  };

  const handleRoomUpdate = (index, updates) => {
    const newRooms = [...data.rooms];
    newRooms[index] = { ...newRooms[index], ...updates };
    handleChange("rooms", newRooms);
  };

  const toggleShowMore = (id) => {
    setShowMoreBeds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const roomFeatureOptions = [
    ...(isACAvailable ? ["Air conditioning"] : []),
    ...(isCeilingFanAvailable ? ["Ceiling fan"] : []),
    "TV", "Smart TV", "Wardrobe", "Desk", "Safe", "Mini Fridge", "Blackout Curtains", "Balcony Access", "Ensuite Bathroom", "Nightstand", "Other"
  ];

  const livingRoomFeatures = [
    ...(isACAvailable ? ["Air conditioning"] : []),
    ...(isCeilingFanAvailable ? ["Ceiling fan"] : []),
    "Sofa", "Sofa bed", "Armchair", "Coffee table", "Side table", "TV", "Smart TV", "Fireplace", "Dining table", "Desk", "Other"
  ];

  return (
    <div className="flex flex-col gap-8">
      <Card title="Bedroom" icon={Layout} subtitle="Tell us about your bedrooms and sleeping setup." isLight={isLight}>
        <div className="flex flex-col gap-10">
          <div className="max-w-xs">
            <Stepper 
              label="Number of Bedrooms" required 
              value={data.bedrooms}
              isLight={isLight}
              onChange={handleBedroomsCountChange}
            />
          </div>

          <div className="flex flex-col gap-12">
            {data.rooms.map((room, index) => (
              <div key={room.id} className={`pt-10 first:pt-0 border-t first:border-t-0 ${isLight ? 'border-neutral-100' : 'border-white/5'}`}>
                <div className="flex flex-col gap-8">
                  <div className="flex items-center justify-between">
                    <h3 className={`text-xl font-bold ${isLight ? 'text-neutral-900' : 'text-neutral-100'}`}>Bedroom {index + 1}</h3>
                    <div className={`h-px flex-1 mx-6 ${isLight ? 'bg-neutral-100' : 'bg-white/5'}`} />
                  </div>

                  <div className="flex flex-col gap-4">
                    <label className={`text-xs font-semibold uppercase tracking-wider ${isLight ? 'text-neutral-500' : 'text-neutral-400'}`}>
                      Where can people sleep? <span className="text-red-500">*</span>
                    </label>
                    {(() => {
                      const totalBeds = Object.values(room.beds).reduce((a, b) => a + (typeof b === 'number' ? b : 0), 0);
                      const isInvalid = showValidation && totalBeds === 0;
                      return (
                        <>
                          <div className={`grid grid-cols-1 sm:grid-cols-2 gap-4 p-2 rounded-3xl transition-all ${isInvalid ? "bg-red-500/5 ring-1 ring-red-500/20" : ""}`}>
                            <BedTile label="Twin bed" icon={Bed} value={room.beds.twin} isLight={isLight} onChange={(val) => handleRoomUpdate(index, { beds: { ...room.beds, twin: val } })} />
                            <BedTile label="Full bed" icon={Bed} value={room.beds.full} isLight={isLight} onChange={(val) => handleRoomUpdate(index, { beds: { ...room.beds, full: val } })} />
                            <BedTile label="Queen bed" icon={Bed} value={room.beds.queen} isLight={isLight} onChange={(val) => handleRoomUpdate(index, { beds: { ...room.beds, queen: val } })} />
                            <BedTile label="King bed" icon={Bed} value={room.beds.king} isLight={isLight} onChange={(val) => handleRoomUpdate(index, { beds: { ...room.beds, king: val } })} />
                            {showMoreBeds[room.id] && (
                              <>
                                <BedTile label="Sofa bed" icon={Sofa} value={room.beds.sofa} isLight={isLight} onChange={(val) => handleRoomUpdate(index, { beds: { ...room.beds, sofa: val } })} />
                                <BedTile label="Bunk bed" icon={Bunk} value={room.beds.bunk} isLight={isLight} onChange={(val) => handleRoomUpdate(index, { beds: { ...room.beds, bunk: val } })} />
                              </>
                            )}
                          </div>
                          {isInvalid && <span className="text-[10px] font-bold text-red-500 mt-1 uppercase tracking-wider">At least one bed must be selected</span>}
                        </>
                      );
                    })()}
                    <button type="button" onClick={() => toggleShowMore(room.id)} className="self-start flex items-center gap-2 text-sm font-medium text-blue-500 hover:text-blue-400 transition-colors px-2">
                      {showMoreBeds[room.id] ? (
                        <>Show less <ChevronUp className="w-4 h-4" /></>
                      ) : (
                        <>Show more <ChevronDown className="w-4 h-4" /></>
                      )}
                    </button>
                  </div>

                  <TagGroup 
                    label="Features" options={roomFeatureOptions}
                    value={room.features} isLight={isLight}
                    onChange={(val) => handleRoomUpdate(index, { features: val })}
                  />
                  {room.features.includes("Other") && (
                    <Input 
                      label="Specify other features" placeholder="e.g., Desk lamp, Alarm clock..." required
                      value={room.otherFeatures} isLight={isLight}
                      showValidation={showValidation}
                      onChange={(e) => handleRoomUpdate(index, { otherFeatures: e.target.value })}
                    />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Card>

      <Card title="Living Room" icon={Layout} subtitle="Tell us about your living room and what's in it." isLight={isLight}>
        <Toggle 
          label="Is a living room available?" 
          value={data.livingRoom.available} isLight={isLight}
        onChange={(val) => {
          const updates = { available: val };
          if (!val) {
            updates.features = [];
            updates.otherFeatures = "";
          }
          updateData("livingSpaces", {
            ...data,
            livingRoom: { ...data.livingRoom, ...updates }
          });
        }}
        >
          <div className="flex flex-col gap-8 mt-4">
            <TagGroup 
              label="Features" options={livingRoomFeatures}
              value={data.livingRoom.features} isLight={isLight}
              onChange={(val) => handleNestedChange("livingRoom", "features", val)}
            />
            {data.livingRoom.features.includes("Other") && (
              <Input 
                label="Specify other features" placeholder="e.g., Board games, Bookshelf..." required
                value={data.livingRoom.otherFeatures} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleNestedChange("livingRoom", "otherFeatures", e.target.value)}
              />
            )}
          </div>
        </Toggle>
      </Card>

      <Card title="Leisure Amenities" icon={Joystick} subtitle="Extra facilities for guests to enjoy." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <TagGroup 
            label="Available Leisure Facilities" 
            options={["Sauna", "indoor pool", "Hot tub", "Gym", "Billiards", "Ping Pong", "Playroom", "Board games", "Other"]}
            value={data.leisure.features} isLight={isLight}
            onChange={(val) => handleNestedChange("leisure", "features", val)}
          />
          {data.leisure.features.includes("Other") && (
            <Input 
              label="Specify other leisure amenities" placeholder="e.g., Guitar, Chess set..." required
              value={data.leisure.otherFeatures} isLight={isLight}
              showValidation={showValidation}
              onChange={(e) => handleNestedChange("leisure", "otherFeatures", e.target.value)}
            />
          )}
        </div>
      </Card>

      <Card title="Outdoor Spaces" icon={Sun} subtitle="Let guests know about any outdoor areas at your property." isLight={isLight}>
        <Toggle 
          label="Outdoor space available?" 
          value={data.outdoor.available} isLight={isLight}
          onChange={(val) => {
            const updates = { available: val };
            if (!val) {
              updates.types = [];
              updates.otherTypes = "";
              updates.features = [];
              updates.otherFeatures = "";
            }
            updateData("livingSpaces", {
              ...data,
              outdoor: { ...data.outdoor, ...updates }
            });
          }}
        >
          <div className="flex flex-col gap-8">
            <TagGroup 
              label="What outdoor spaces are available?" required 
              options={["Balcony", "Terrace", "Yard", "Garden", "Rooftop", "Patio", "Other"]}
              value={data.outdoor.types} isLight={isLight}
              showValidation={showValidation}
              onChange={(val) => handleNestedChange("outdoor", "types", val)}
            />
            {data.outdoor.types.includes("Other") && (
              <Input 
                label="Specify other space types" placeholder="e.g., Veranda, Gazebo..." required
                value={data.outdoor.otherTypes} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleNestedChange("outdoor", "otherTypes", e.target.value)}
              />
            )}
            <TagGroup 
              label="Features" 
              options={["Table & Chairs", "Outdoor Sofa", "Sunbeds", "Umbrella", "BBQ", "Outdoor pool", "Hot tub", "Sea View", "Other"]}
              value={data.outdoor.features} isLight={isLight}
              onChange={(val) => handleNestedChange("outdoor", "features", val)}
            />
            {data.outdoor.features.includes("Other") && (
              <Input 
                label="Specify other features" placeholder="e.g., Hammock, Outdoor shower..." required
                value={data.outdoor.otherFeatures} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleNestedChange("outdoor", "otherFeatures", e.target.value)}
              />
            )}
          </div>
        </Toggle>
      </Card>

      <Card title="Family & Children" icon={Baby} subtitle="Let families know if your property is child-friendly." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Are children allowed?" 
            value={data.family.allowed} isLight={isLight}
            onChange={(val) => {
              const updates = { allowed: val };
              if (!val) {
                updates.babyCot = false;
                updates.babyCotDetails = "";
                updates.highChair = false;
                updates.otherEquipment = "";
              }
              updateData("livingSpaces", {
                ...data,
                family: { ...data.family, ...updates }
              });
            }}
          >
            <div className="flex flex-col gap-8 mt-4">
              <Toggle 
                label="Baby cot/crib available?" 
                value={data.family.babyCot} isLight={isLight}
                onChange={(val) => handleNestedChange("family", "babyCot", val)}
              >
                <Input 
                  label="Details" placeholder="Available on request, €10 flat fee" required
                  value={data.family.babyCotDetails} isLight={isLight}
                  onChange={(e) => handleNestedChange("family", "babyCotDetails", e.target.value)}
                />
              </Toggle>
              <Toggle 
                label="High chair available?" 
                value={data.family.highChair} isLight={isLight}
                onChange={(val) => handleNestedChange("family", "highChair", val)}
              />
              <Input 
                label="Other baby equipment" 
                placeholder="Baby bath, stroller" 
                value={data.family.otherEquipment} isLight={isLight}
                onChange={(e) => handleNestedChange("family", "otherEquipment", e.target.value)}
              />
            </div>
          </Toggle>
        </div>
      </Card>
    </div>
  );
};
